import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { PWAPrompt } from "@/components/PWAPrompt";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthGuard } from "@/components/AuthGuard";
import { RoleGuard } from "@/components/RoleGuard";
import { SubscriptionGuard } from "@/components/SubscriptionGuard";
import { useEffect, useState, type ReactNode } from "react";
import { useSession, startIdleWatch } from "@/state/session";
import { supabase } from "@/integrations/supabase/client";
import { devLog } from "@/lib/utils";
import Login from "./pages/Login";
import Auth from "./pages/Auth";
import Billing from "./pages/Billing";
import ResetPassword from "./pages/ResetPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Ajuda from "./pages/Ajuda";
import Dashboard from "./pages/Dashboard";
import Cadastro from "./pages/Cadastro";
import Vendas from "./pages/Vendas";
import Entrada from "./pages/Entrada";
import EntradaDetalhes from "./pages/EntradaDetalhes";
import Estoque from "./pages/Estoque";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Users from "./pages/Users";
import Produtos from "./pages/Produtos";
import Estabelecimentos from "./pages/Estabelecimentos";
import Clientes from "./pages/Clientes";
import Receitas from "./pages/Receitas";
import Fornada from "./pages/Fornada";
import Tabelas from "./pages/Tabelas";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // 🔍 DEBUG: desabilitado temporariamente
      retry: 1,
    },
  },
});

function AuthProvider({ children }: { children: ReactNode }) {
  const { setSupabaseSession, authIsReady } = useSession();
  const [initializing, setInitializing] = useState(true);

  // ✅ ETAPA 2.2: Iniciar IdleWatch
  useEffect(() => {
    const stopIdle = startIdleWatch();
    return () => stopIdle?.();
  }, []);
  
  // Enable theme transitions after initial paint (OPTIMIZED)
  useEffect(() => {
    // Duplo rAF garante execução APÓS primeiro paint, sem delay perceptível
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.add('theme-transitions-enabled');
      });
    });
  }, []);

  useEffect(() => {
    devLog('🔧 AuthProvider MOUNT');

    // Log inicial da sessão
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      devLog('📦 INITIAL getSession:', { hasSession: !!session, userId: session?.user?.id });
      await setSupabaseSession(session);
      setInitializing(false);
    });

    // Listener de auth com logs detalhados + dedupe pré-filter
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      devLog('🔄 AUTH_EVENT', { 
        event, 
        hasSession: !!session, 
        userId: session?.user?.id,
        visibility: document.visibilityState,
        timestamp: new Date().toISOString()
      });
      
      // ✅ ETAPA 2.3: Dedupe pré-filter
      const current = useSession.getState().user?.id;
      const incoming = session?.user?.id;
      
      if (event === 'SIGNED_IN' && current && incoming && current === incoming) {
        devLog('⏭️ onAuthStateChange: sessão idêntica ignorada (pré-filter)');
        return;
      }
      
      setSupabaseSession(session);
    });

    // ✅ ETAPA 2.1: Cancelation guard
    let cancelled = false;
    let visibilityTimeout: NodeJS.Timeout | null = null;

    // Listener de visibility change com debounce + cancelation guard
    const handleVisibilityChange = async () => {
      devLog('👁️ VISIBILITY_CHANGE', document.visibilityState);
      
      if (document.visibilityState === 'visible') {
        if (visibilityTimeout) clearTimeout(visibilityTimeout);
        
        visibilityTimeout = setTimeout(async () => {
          const { data } = await supabase.auth.getSession();
          if (cancelled) return; // ✅ NOVO: proteção pós-desmontagem
          devLog('👁️ FOCUS getSession (debounced):', { 
            hasSession: !!data.session, 
            userId: data.session?.user?.id 
          });
        }, 300);
      }
    };

    const handleFocus = () => {
      devLog('🎯 WINDOW_FOCUS');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true; // ✅ NOVO
      devLog('🔧 AuthProvider UNMOUNT');
      if (visibilityTimeout) clearTimeout(visibilityTimeout);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [setSupabaseSession]);

  // Aguardar inicialização antes de renderizar rotas
  if (initializing || !authIsReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-sm text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <PWAPrompt />
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<Navigate to="/auth?mode=login" replace />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/ajuda" element={<Ajuda />} />
                <Route
                  path="/billing"
                  element={
                    <AuthGuard>
                      <Billing />
                    </AuthGuard>
                  }
                />
                
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Dashboard />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/cadastro"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Cadastro />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/vendas/*"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Vendas />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route path="/entrada" element={<AuthGuard><SubscriptionGuard><RoleGuard><Entrada /></RoleGuard></SubscriptionGuard></AuthGuard>} />
                <Route path="/entrada/:id" element={<AuthGuard><SubscriptionGuard><RoleGuard><EntradaDetalhes /></RoleGuard></SubscriptionGuard></AuthGuard>} />
                <Route path="/estoque" element={<AuthGuard><SubscriptionGuard><RoleGuard><Estoque /></RoleGuard></SubscriptionGuard></AuthGuard>} />
                <Route
                path="/profile"
                element={
                  <AuthGuard>
                    <Profile />
                  </AuthGuard>
                }
              />
              <Route
                path="/settings"
                element={
                  <AuthGuard>
                    <Settings />
                  </AuthGuard>
                }
              />
                <Route
                  path="/users"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Users />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/produtos"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Produtos />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/estabelecimentos"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Estabelecimentos />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/clientes"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Clientes />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/receitas"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Receitas />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/fornada"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard>
                          <Fornada />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/tabelas"
                  element={
                    <AuthGuard>
                      <SubscriptionGuard>
                        <RoleGuard require={["admin"]}>
                          <Tabelas />
                        </RoleGuard>
                      </SubscriptionGuard>
                    </AuthGuard>
                  }
                />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
